import * as React from 'react';
import { useCallback } from 'react';

import { MAX_MULTIPLE_CHOICE_TEXT_LENGTH } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import {
  ImageAddMethod,
  ImageUploadContext,
} from '~analytics/adapters/ImageEventAdapterTypes';
import { PaywallScreens } from '~analytics/types';
import { IconButton } from '~shared/components/buttons';
import { Checkbox } from '~shared/components/Checkbox';
import { IconX } from '~shared/components/icons/standard';
import ImageFieldDisplay from '~shared/components/ImageField/ImageFieldDisplay';
import ImagePreviewer from '~shared/components/ImageField/ImagePreviewer';
import QuillEditor from '~shared/components/quill/QuillEditor';
import { QuillTextChangeHandler } from '~shared/components/quill/quillHooks';
import { RichTextRenderer } from '~shared/components/RichTextRenderer';
import { useUploadImage } from '~teacher/hook/upload/useUploadImage';
import { QuizAnswer } from '~teacher/models/quizzes';
import { proCheck } from '~teacher/util/proCheck';

import {
  changeAnswerText,
  deleteAnswer,
  toggleAnswerCorrect,
  updateAnswerImage,
} from '../../slices/quiz/quizSlice';
import { openPaywall } from '../../slices/teacher/teacherSlice';

interface Props {
  answer: QuizAnswer;
  answerPlaceholder: string;
  isEditing: boolean;
  isDisabled: boolean;
}

const ContentOrFields = ({
  isEditing,
  isDisabled,
  answer,
  answerPlaceholder,
}: Props) => {
  if (isEditing) {
    return (
      <Field
        id={`${answer.id}`}
        isCorrect={answer.isCorrect}
        isDisabled={isDisabled}
        text={answer.text}
        image={answer.image}
        answerPlaceholder={answerPlaceholder}
      />
    );
  }

  return <Content text={answer.text} image={answer.image} />;
};

export default ContentOrFields;

function ContentOrFieldsWrapper({
  children,
  isEditing,
}: {
  children: React.ReactNode;
  isEditing: boolean;
}) {
  const wrapperClass = cx(
    'relative z-10 col-span-2 col-start-3 max-w-full min-w-0 pl-2',
    {
      'flex flex-1 items-start': isEditing,
    }
  );

  return <div className={wrapperClass}>{children}</div>;
}

function Content({
  text,
  image,
}: {
  text: string;
  image: QuizAnswer['image'];
}) {
  return (
    <ContentOrFieldsWrapper isEditing={false}>
      <div
        className={`relative flex-1 max-w-full min-w-0 quill-field is-readonly ${
          image && 'is-image-set'
        }}`}
      >
        <div className="relative z-10 flex-1 max-w-full py-2 quill-wrapper">
          {text ? (
            <RichTextRenderer
              className="font-sans text-blue-900 text-base leading-8 ql-editor"
              htmlText={text}
            />
          ) : null}
          {image ? (
            <ImagePreviewer
              containerStyle="inline-flex my-2 rounded-1"
              imageStyle="max-h-64 rounded-1"
              imageURL={image?.urlS}
              showOutline
              allowZoom
              zoomImageURL={image?.url}
            />
          ) : null}
        </div>
      </div>
    </ContentOrFieldsWrapper>
  );
}

function useHandlePaste({
  handleUpload,
  quillRef,
  isDisabled,
}: {
  isDisabled: boolean;
  handleUpload: (file: File) => void;
  quillRef: React.RefObject<HTMLDivElement>;
}) {
  // Handle paste events directly to access clipboard data
  React.useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (isDisabled) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const file = items[i].getAsFile();
          if (file) {
            try {
              handleUpload(file);
            } catch (error) {
              console.error('Failed to upload pasted image:', error);
            }
          }
          break;
        }
      }
    };

    const editor = quillRef.current;
    editor?.addEventListener('paste', handlePaste);
    return () => editor?.removeEventListener('paste', handlePaste);
  }, [handleUpload, isDisabled, quillRef]);
}

function Field({
  id,
  isCorrect,
  isDisabled,
  text,
  image,
  answerPlaceholder,
}: {
  id: string;
  isCorrect: boolean;
  isDisabled: boolean;
  text: string;
  image: QuizAnswer['image'];
  answerPlaceholder: string;
}) {
  const quillRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const uploadImage = useUploadImage({ context: ImageUploadContext.Answer });
  const handleUpload = useCallback(
    (file: File) => {
      uploadImage.mutate(
        { files: [file], addMethod: ImageAddMethod.CopyPaste },
        {
          onSuccess: (url) => {
            dispatch(updateAnswerImage({ answerId: id, url: url }));
          },
        }
      );
    },
    [dispatch, id, uploadImage]
  );

  useHandlePaste({
    handleUpload: handleUpload,
    quillRef: quillRef,
    isDisabled: !!image,
  });

  const handleChangeCorrect = React.useCallback(() => {
    dispatch(toggleAnswerCorrect({ answerId: id }));
  }, [id, dispatch]);

  const handleTextChange = React.useCallback<QuillTextChangeHandler>(
    ({ html }: { html: string; text: string }) => {
      const answerText = html;
      dispatch(changeAnswerText({ answerId: id, answerText }));
    },
    [dispatch, id]
  );

  const showAdvancedQuizEditor = proCheck.canShowAdvancedQuizEditor();

  const handleImageChange = React.useCallback(
    (url) => {
      dispatch(updateAnswerImage({ answerId: id, url }));
    },
    [dispatch, id]
  );
  const handleDeleteAnswer = React.useCallback(() => {
    return dispatch(deleteAnswer({ answerId: id }));
  }, [dispatch, id]);

  return (
    <ContentOrFieldsWrapper isEditing={true}>
      <label className="flex-none flex items-center w-11 h-12 cursor-pointer">
        <Checkbox
          size="large"
          id={`answer-${id}`}
          checked={isCorrect}
          appearance="positive"
          value={id}
          disabled={isDisabled}
          onChange={handleChangeCorrect}
          aria-label={translate('Correct')}
        />
      </label>
      <QuillEditor
        ref={quillRef}
        initialHtml={text}
        placeholder={answerPlaceholder}
        onTextChange={handleTextChange}
        isReadOnly={isDisabled}
        maxLength={MAX_MULTIPLE_CHOICE_TEXT_LENGTH}
        hasImageField
        isImageSet={!!image}
      >
        <ImageFieldDisplay
          key={image?.id}
          onImageChange={handleImageChange}
          isReadOnly={isDisabled}
          image={image}
          imageFieldLabel={translate('Answer Image')}
          imageClearLabel={translate('Remove Image')}
          imageFieldDisabled={isDisabled}
          context={ImageUploadContext.Answer}
          addImageIntercept={
            !showAdvancedQuizEditor
              ? () =>
                  dispatch(
                    openPaywall({
                      paywallTouch: [PaywallScreens.QuizEditAddAnswerImage],
                    })
                  )
              : null
          }
        />
      </QuillEditor>
      <IconButton
        withAltBackground
        appearance="ghost"
        isRisky
        aria-label={translate('Remove Answer')}
        disabled={isDisabled}
        onClick={handleDeleteAnswer}
      >
        <IconX />
      </IconButton>
    </ContentOrFieldsWrapper>
  );
}
